import { useState } from 'react'
import type { FormEvent } from 'react'
import { api, HttpError } from '../api/client'

interface Props {
  /** Called after a project is created, so the caller can reload its list. */
  onCreated: () => void | Promise<void>
}

// Tags on by default: most projects want them, and the ones that do not
// are the exception a manager opts out of.
const EMPTY = { name: '', description: '', allowsTags: true }

/**
 * Creating a project. Its own card, and its own state — nothing here is shared
 * with the list beside it beyond the reload callback, so a failed create shows
 * its error next to the fields that caused it rather than above the list.
 */
export function NewProjectForm({ onCreated }: Props) {
  const [form, setForm] = useState(EMPTY)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleSubmit(event: FormEvent) {
    event.preventDefault()
    if (!form.name.trim() || !form.description.trim()) return

    setSaving(true)
    try {
      await api.createProject(form)
      setForm(EMPTY)
      setError(null)
      await onCreated()
    } catch (err) {
      // The API returns a readable message for a duplicate name, so show it.
      setError(err instanceof HttpError ? err.message : 'Could not create that project.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <form className="card form" onSubmit={handleSubmit}>
      <h2>New project</h2>

      <label>
        <span>Name</span>
        <input
          required
          maxLength={120}
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          placeholder="Design"
        />
      </label>

      <label>
        <span>Description</span>
        <textarea
          required
          rows={2}
          maxLength={2000}
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
          placeholder="What belongs in this project."
        />
      </label>

      <label className="check">
        <input
          type="checkbox"
          checked={form.allowsTags}
          onChange={(e) => setForm({ ...form, allowsTags: e.target.checked })}
        />
        <span>Allow tags</span>
      </label>

      {error && <p className="error">{error}</p>}

      <button type="submit" disabled={saving}>
        {saving ? 'Adding…' : 'Add project'}
      </button>
    </form>
  )
}
